import React, {useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Typeahead, Token} from 'react-bootstrap-typeahead';
import {capitalize} from 'lodash';
import InputGroup from 'react-bootstrap/InputGroup';
import Form from 'react-bootstrap/Form';

import 'react-bootstrap-typeahead/css/Typeahead.css';

import './styles.scss';

const MultiSelect = (props) => {
  const {
    label,
    placeholder,
    handleChange,
    type,
    error = false,
    labelKey = 'label',
    selected,
    selectAll = false,
    multiple = true,
    nullPrefix = '',
    hasChildren = false,
    filterByKeys = [],
    renderCustomCategoryToken = false,
    keepFocusAfterSelect = false,
    renderMenu,
    handleFocus,
    handleBlur,
    isFetching = false,
    clearOnSelect = false,
    elemsBeforeTypeahead,
    elemsAfterTypehead,
    typeAheadProps = {},
    renderElemsInsideInputGroup = false,
    selectHintOnEnter = false,
    clearButton = false,
    renderToken,
    renderMenuItemChildren,
    renderInput,
    dataTestId,
  } = props;

  const [inputText, setInputText] = useState('');
  const typeaheadRef = useRef(null);

  // accessibilty ID should use label and fallback to type
  const accessibilityId = (typeof label === 'string' && label) || type || 'default';
  const inputId = `multiselect-${accessibilityId}-input`;
  const labelId = `multiselect-${accessibilityId}-label`;

  // Accessibility description of current selected state passed to the multiselect input box
  // used to set both aria-describedby and aria-live attributes
  const selectedDescription = useMemo(() => {
    if (!selected || selected.length === 0) {
      return '';
    }
    const selectedValues = selected.map((s) => (typeof s === 'string' ? s : s[labelKey] || '')).join(', ');
    return `${accessibilityId}: ${selectedValues} selected`;
  }, [selected, accessibilityId, labelKey]);

  const printErrors = (error) => {
    return error && error.messages.map((each) => {
      return (
        <Form.Text
          key={`multiselect-error-${each.text}`}
          className={'text-danger error'}
        >
          {each.text}
        </Form.Text>
      );
    });
  };

  /**
   * Append the nestLevel for each node so that a list can be properly indented
   *
   * @param {Array} list contains a childid and a parentid to be parsed for depth
   * @param {String} childId is the name of the key for the childid in the list
   * @param {String} parentId is the name of the key for the parentid in the list
   * @returns {Array} [...list, nestLevel]
   */
  const appendDepth = (list, childId, parentId) => {
    const dictionary = new Map();

    list.forEach((item) => {
      dictionary.set(item[childId], {...item, depth: 0});
    });

    list.forEach((item) => {
      let parentid = item[parentId];
      let depth = 0;

      while (parentid) {
        parentid = (dictionary.get(parentid) || {})[parentId];
        depth++;

        // Escape
        if (depth > 10) {
          parentid = 0;
        }
      }

      dictionary.set(item[childId], {...item, depth});
    });

    // Update list
    return list.map((item) => {
      return {...item, nestLevel: dictionary.get(item[childId])['depth']};
    });
  };

  let {options = []} = props;

  const typeAheadPropsInternal = {...typeAheadProps};

  const nullOption = (nullPrefix === '')
    ? {[labelKey]: capitalize(`All ${label}`), id: null}
    : {[labelKey]: capitalize(nullPrefix), id: null};

  // Work around for accessible X button on multiselect pillboxes
  // rbt- prefix classes such as rbt-token used here to replicate react-bootstrap-typeahead own CSS
  // and injects custom accessibility attributes on top
  const accessibleRenderToken = (option, {onRemove}, idx) => {
    const optionLabel = option[labelKey] || '';
    return (
      <div key={idx} className="rbt-token rbt-token-removeable">
        <div className="rbt-token-label">{optionLabel}</div>
        <button
          type="button"
          className="close btn-close rbt-close rbt-token-remove-button"
          tabIndex={0}
          aria-label={`Remove ${optionLabel}`}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(option);
          }}
        >
          <span aria-hidden="true" className="rbt-close-content">&times;</span>
        </button>
      </div>
    );
  };

  // Tokens for selected options that appear in typeahead
  // default to rendering token with accessibility if no custom renderer is set
  if (renderToken) {
    typeAheadPropsInternal.renderToken = renderToken;
  } else if (renderCustomCategoryToken) {
    typeAheadPropsInternal.renderToken = (option, props) => (
      <Token key={option.id} title={option.keywords} onRemove={props.onRemove}>
        {option[labelKey]}
      </Token>
    );
  } else {
    typeAheadPropsInternal.renderToken = accessibleRenderToken;
  }

  if (renderInput) {
    typeAheadPropsInternal.renderInput = renderInput;
  }

  if (renderMenuItemChildren) {
    typeAheadPropsInternal.renderMenuItemChildren = renderMenuItemChildren;
  }

  if (filterByKeys.length) {
    typeAheadPropsInternal.filterBy = [labelKey, ...filterByKeys];
  }

  // Append nestLevel to indent options
  switch (labelKey) {
    case 'plantypename':
      options = appendDepth(options, 'id', 'parentid');
  }

  if (labelKey === 'categoryname' || hasChildren) {
    typeAheadPropsInternal.filterBy = [labelKey, 'parentname', ...filterByKeys];
  }

  if (typeof renderMenu == 'function') {
    typeAheadPropsInternal.renderMenu = renderMenu;
  }

  if (typeof handleBlur == 'function') {
    typeAheadPropsInternal.onBlur = () => {
      const instance = typeaheadRef.current.getInstance();
      handleBlur(instance);
    };
  }

  if (typeof handleFocus == 'function') {
    typeAheadPropsInternal.onFocus = () => {
      const instance = typeaheadRef.current.getInstance();
      handleFocus(instance);
    };
  }

  if (keepFocusAfterSelect) {
    typeAheadPropsInternal.onInputChange = (text) => {
      setInputText(text);
    };
  }

  let selectOptions = options;
  if (selectAll) {
    selectOptions = [nullOption, ...options];
  }

  if (options.length < 1) {
    typeAheadPropsInternal.disabled = true;
  }

  typeAheadPropsInternal.selectHintOnEnter = selectHintOnEnter;

  const selectedDescriptionId = `multiselect-${accessibilityId}-description`;

  typeAheadPropsInternal.inputProps = {
    ...typeAheadPropsInternal.inputProps,
    id: inputId,
    'aria-labelledby': labelId,
    ...(selectedDescription && {'aria-describedby': selectedDescriptionId}),
  };

  const noOptionsPlaceholder = isFetching ? 'Loading...' : 'No options available.';

  const typeAhead = (
    <Typeahead
      id={`multiselect-${accessibilityId}`}
      ref={typeaheadRef}
      multiple={multiple}
      clearButton={clearButton}
      options={selectOptions}
      selected={selected === null ? [nullOption] : selected}
      placeholder={options.length > 0 ? placeholder : noOptionsPlaceholder}
      onChange={(selected) => {
        handleChange(selected, type);

        if (!typeaheadRef.current) {
          return;
        }

        if (clearOnSelect) {
          typeaheadRef.current.getInstance().clear();
          typeaheadRef.current.getInstance()._input.value = inputText;
        }
      }}
      {...typeAheadPropsInternal}
    />
  );

  return (
    <Form.Group data-component={'MultiSelect'} data-testid={dataTestId} className='mb-3'>
      {label && <Form.Label id={labelId} htmlFor={inputId} className="fw-bold">{label}</Form.Label>}
      {renderElemsInsideInputGroup ? (
        <InputGroup>
          {elemsBeforeTypeahead}
          {typeAhead}
          {elemsAfterTypehead}
        </InputGroup>
      ) : (
        <>
          {elemsBeforeTypeahead}
          {typeAhead}
          {elemsAfterTypehead}
        </>
      )}
      {printErrors(error)}
      {/* Hidden span for updating dynamic accessible value of multiselect */}
      <span id={selectedDescriptionId} aria-live="polite" className="visually-hidden">{selectedDescription}</span>
    </Form.Group>
  );
};

MultiSelect.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  placeholder: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  type: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  labelKey: PropTypes.string,
  selected: PropTypes.array,
  selectAll: PropTypes.bool,
  multiple: PropTypes.bool,
  nullPrefix: PropTypes.string,
  hasChildren: PropTypes.bool,
  filterByKeys: PropTypes.array,
  renderCustomCategoryToken: PropTypes.bool,
  keepFocusAfterSelect: PropTypes.bool,
  renderMenu: PropTypes.func,
  handleFocus: PropTypes.func,
  handleBlur: PropTypes.func,
  isFetching: PropTypes.bool,
  clearOnSelect: PropTypes.bool,
  elemsBeforeTypeahead: PropTypes.node,
  elemsAfterTypehead: PropTypes.node,
  typeAheadProps: PropTypes.object,
  renderElemsInsideInputGroup: PropTypes.bool,
  selectHintOnEnter: PropTypes.bool,
  clearButton: PropTypes.bool,
  renderToken: PropTypes.func,
  renderMenuItemChildren: PropTypes.func,
  renderInput: PropTypes.func,
  options: PropTypes.array,
  dataTestId: PropTypes.string,
};

export default MultiSelect;
